import { getTimeZoneHeaders } from "utils/timeZone";
import { client as sharedClient } from "@vyne-shared/api/VynePayments";
import type {
    AppointmentResults,
    BulkRequestQueryResponse,
    BulkRequestSummaries,
    BusinessEntityInfo,
    ConnectedAccountSessionResponse,
    ExistingInsuranceResult,
    ExistingPatientResult,
    LegalBusinessInfoResult,
    Metric,
    OnboardingResult,
    PMSPatientSummaryResult,
    PatientActivityResult,
    PatientHistoryResult,
    PatientMergeResult,
    PatientPortalLinkResult,
    PatientSummaryResult,
    PaymentReportResult,
    ProviderConfigResponse,
    ProviderNotificationPreviewResult,
    ProviderSettingsPatientResponse,
    ProviderSettingsPracticeResponse,
    StripeAccountStatusResult,
    StripeRatesResult,
    StripeSettingsFormLinkResult,
    SyncStatusQueryResult,
    TerminalResult,
    TransactionRefundResult,
} from "./responses";
import type {
    BulkRequestItem,
    PatientBasicUpdateRequest,
    PatientManualProvisionRequest,
    PatientMergeRequest,
    PatientUpdateRequest,
    PaymentReportPatchRequest,
    ProviderConfigRequest,
    ProviderSettingsPatientRequest,
    ProviderSettingsPracticeRequest,
    ProvisionInsurancePaymentRequest,
    RefundStatusRequest,
    TerminalCreateRequest,
    TerminalUpdateRequest,
} from "./requests";
import type { StrictlyBasisPoints } from "@vyne-shared/utils/currency";

/** The single shared axios instance used for making API calls. */
export const client = sharedClient;

/** The Payments API wrapper. */
export const api = {
    customer: {
        /**
         * Sign up the active Customer for Payments by signing the merchant agreement.
         * @returns The Payments sign-up URL.
         */
        signupMerchantAgreement: async () => {
            await client.post("customer/signup");
        },
    },
    patient: {
        /**
         * Send a balance update message to a P/RP.
         * @param patientId The ID of the P/RP to notify.
         */
        sendBalance: async (patientId: number) => {
            await client.post(`patient/${patientId}/sendBalance`);
        },

        /**
         * List event history for a specific Patient/Responsible Party in the system.
         * @param patientId The ID of the P/RP to query.
         * @param signal Optional abort signal to cancel the request.
         * @returns Response including event history data.
         */
        history: async (patientId: number, signal?: AbortSignal) =>
            (await client.get<PatientHistoryResult[]>(`patient/${patientId}/History`, { signal })).data,

        /**
         * List a Patient/Responsible Party's single transaction history in the system.
         * @param patientId The ID of the P/RP of the transaction.
         * @param transactionId The ID of the transaction to query for.
         * @param signal Optional abort signal to cancel the request.
         * @returns Response including transaction amount, refund status, and partial refund history.
         */
        singleTransactionHistory: async (patientId: number, transactionId: number, signal?: AbortSignal) =>
            (
                await client.get<PatientHistoryResult>(`patient/${patientId}/History/Transaction/${transactionId}`, {
                    signal,
                })
            ).data,

        /**
         * Get Patient Portal link for a particular Patient/Responsible Party.
         * @param patientId The ID of the P/RP to get a portal link for.
         * @param signal Optional abort signal to cancel the request.
         * @returns Portal link data; currently just the link itself.
         */
        portalLink: async (patientId: number, signal?: AbortSignal) =>
            (await client.get<PatientPortalLinkResult>(`patient/${patientId}/portalLink`, { signal })).data,
    },
    provider: {
        /**
         * Get the configuration data for the current Payments Provider.
         * @param signal Optional abort signal to cancel the request.
         * @returns The configuration data for the current Provider.
         */
        getConfiguration: async (signal?: AbortSignal) =>
            (await client.get<ProviderConfigResponse>("provider/configuration", { signal })).data,

        /**
         * Configure a Payments Provider to begin using the system.
         * @param configData Request data to perform configuration.
         */
        setConfiguration: async (configData: ProviderConfigRequest) => {
            await client.put("provider/configuration", configData);
        },

        /**
         * Get the patient settings data for the current Payments Provider.
         * @param signal Optional abort signal to cancel the request.
         * @returns The customization data for the current Provider.
         */
        patientSettings: async (signal?: AbortSignal) =>
            (await client.get<ProviderSettingsPatientResponse>("provider/settings/patient", { signal })).data,

        /**
         * Set the patient settings for a Payments Provider.
         * @param customizationData Request data to perform customization.
         */
        setPatientSettings: async (customizationData: ProviderSettingsPatientRequest) => {
            await client.put("provider/settings/patient", customizationData);
        },

        /**
         * Get the practice settings data for the current Payments Provider.
         * @param signal Optional abort signal to cancel the request.
         * @returns The customization data for the current Provider.
         */
        practiceSettings: async (signal?: AbortSignal) =>
            (await client.get<ProviderSettingsPracticeResponse>("provider/settings/practice", { signal })).data,

        /**
         * Set the practice settings for a Payments Provider.
         * @param customizationData Request data to perform customization.
         */
        setPracticeSettings: async (customizationData: ProviderSettingsPracticeRequest) => {
            await client.put("provider/settings/practice", customizationData);
        },

        /**
         * Set the practice bill pay link for a Payments Provider.
         * @returns The bill pay link.
         */
        setPracticeBillPayLink: async () =>
            (await client.post<string>("provider/settings/practice/bill-pay-link")).data,

        /** Merge a PMS P/RP with an existing Payments P/RP. */
        mergePatient: async (request: PatientMergeRequest) =>
            (await client.post<PatientMergeResult>("provider/merge", request)).data,

        /** Unmerges patient (for manual test purposes). */
        unmergePatient: async (request: number) => {
            await client.delete<PatientMergeResult>("provider/merge", { params: { patientId: request } });
        },

        /**
         * Query some basic business metrics for the system.
         * @param startDateMs Optional start date to limit the metrics display.
         * @param endDateMs Optional end date to limit the metrics display.
         * @param isMarketing Optional flag to indicate if the metrics are for marketing purposes.
         * @param signal Optional abort signal to cancel the request.
         * @returns Metric data provided by the back end.
         */
        metrics: async (
            startDateMs?: number | string,
            endDateMs?: number | string,
            isMarketing?: boolean,
            signal?: AbortSignal,
        ) =>
            (
                await client.get<Metric[]>("provider/metrics", {
                    params: { startDateMs, endDateMs, isMarketing },
                    signal,
                })
            ).data,

        /**
         * List Patient/Responsible Parties and payment activity in the system.
         * @param startDateMs Optional start date to limit the activity display.
         * @param endDateMs Optional end date to limit the activity display.
         * @param patientName Optional filter to apply to the results.
         * @param status Optional transaction request filters to apply to the results.
         * @param signal Optional abort signal to cancel the request.
         * @returns Activity information for the P/RPs in the system.
         */
        patientActivity: async (
            startDateMs?: number,
            endDateMs?: number,
            patientName?: string,
            status?: string[],
            signal?: AbortSignal,
        ) =>
            (
                await client.get<PatientActivityResult[]>("provider/activity", {
                    params: { patientName, startDateMs, endDateMs, status },
                    signal,
                })
            ).data,

        /**
         * Create or update a Patient/Responsible Party and post a new balance transaction.
         * @param transactionData Information on the P/RP and the transaction to post.
         * @returns P/RP identifier (whether already existing or newly created).
         */
        patientTransaction: async (transactionData: PatientUpdateRequest) =>
            (await client.post<number>("provider/patient", transactionData)).data,

        /**
         * Update an existing Patient/Responsible Party without changing balance or posting a transaction.
         * @param updateData Information on the P/RP to update.
         */
        patientUpdate: async (updateData: PatientBasicUpdateRequest) => {
            await client.patch("provider/patient", updateData);
        },

        /**
         * Provision a new P/RP with a zero balance and without any transactions, if a P/RP matching
         * on name and contact doesn't already exist.
         * @param data Information on the P/RP to post.
         * @returns P/RP identifier (whether already existing or newly created).
         */
        provisionManually: async (data: PatientManualProvisionRequest) =>
            (await client.post<number>("provider/provision", data)).data,

        /**
         * Query for specific Patients/Responsible Parties based on name.
         * @param query The name fragment to search for.
         * @param excludeUnlinked If set, P/RPs not linked to PMS patients are not included.
         * @param signal Optional abort signal to cancel the request.
         * @returns Limited number of P/RP entries with basic information.
         */
        queryPatients: async (query: string, excludeUnlinked: boolean, signal?: AbortSignal) =>
            (
                await client.get<ExistingPatientResult[]>("provider/patients", {
                    params: { query, excludeUnlinked },
                    signal,
                })
            ).data,

        /**
         * Get a summary of PMS information for a particular patient.
         * @param pmsId The PMS ID of the patient to look up.
         * @param signal Optional abort signal to cancel the request.
         * @returns PMS information summary.
         */
        pmsSummary: async (pmsId: string, signal?: AbortSignal) =>
            (
                await client.get<PMSPatientSummaryResult>("provider/summary", {
                    params: { pmsId },
                    signal,
                })
            ).data,

        /**
         * Get a summary of information for a particular patient.
         * @param id The Payments ID of the patient to look up.
         * @param signal Optional abort signal to cancel the request.
         * @returns Information summary. May be either integrated or non-integrated.
         */
        summary: async (id: number, signal?: AbortSignal) =>
            (
                await client.get<PatientSummaryResult | PMSPatientSummaryResult>("provider/summary", {
                    params: { patientId: id },
                    signal,
                })
            ).data,

        /**
         * Get PMS integration sync status for current provider.
         * @param signal Optional abort signal to cancel the request.
         * @returns PMS integration sync status for current provider.
         */
        syncStatus: async (signal?: AbortSignal) =>
            (await client.get<SyncStatusQueryResult>("provider/SyncStatus", { signal })).data,

        /**
         * Get a report of payment activity.
         * @param startDateMs Start date for the report.
         * @param endDateMs End date for the report.
         * @param signal Optional abort signal to cancel the request.
         * @returns Report of payment activity, in CSV form, within a Blob.
         */
        paymentReportCsv: async (startDateMs: number, endDateMs: number, signal?: AbortSignal) =>
            (
                await client.get<Blob>("provider/paymentReport", {
                    responseType: "blob",
                    headers: { accept: "text/csv", ...getTimeZoneHeaders() },
                    params: { startDateMs, endDateMs },
                    signal,
                })
            ).data,

        /**
         * Get a report of payment activity.
         * @param startDateMs Start date for the report.
         * @param endDateMs End date for the report.
         * @param patientName Name of patient to search for
         * @param status Array of statuses to filter on
         * @param signal Optional abort signal to cancel the request.
         * @returns Object bearing report of payment activity.
         */
        paymentReportJson: async (
            startDateMs: number,
            endDateMs: number,
            patientName?: string,
            statuses?: string[],
            sources?: string[],
            signal?: AbortSignal,
        ) =>
            (
                await client.get<PaymentReportResult>("provider/paymentReport", {
                    headers: { accept: "application/json", ...getTimeZoneHeaders() },
                    params: { startDateMs, endDateMs, patientName, status: statuses, source: sources },
                    signal,
                })
            ).data,

        /**
         * Mark a payment report row (a Transaction or Refund) as posted or un-posted.
         * @param patch Request to mark posted or mark un-posted a Transaction or Refund.
         * @param signal Optional abort signal to cancel the request.
         * @returns Object bearing the patched row in its entirety. The "rows" array will have exactly one entry.
         */
        paymentReportPatch: async (patch: PaymentReportPatchRequest) =>
            (
                await client.patch<PaymentReportResult>("provider/paymentReport", patch, {
                    headers: { accept: "application/json", ...getTimeZoneHeaders() },
                })
            ).data,

        /**
         * Write back a Transaction to the PMS.
         * @param transactionId The ID of the Transaction to write back.
         * @param signal Optional abort signal to cancel the request.
         * @returns New command ID, which is generally ignored.
         */
        postPmsWriteback: async (transactionId: number) =>
            (await client.post<string>(`provider/WriteBack/${transactionId}`)).data,

        /**
         * Cancel a writeback of a Transaction to the PMS.
         * @param transactionId The ID of the Transaction to cancel the writeback for.
         * @param signal Optional abort signal to cancel the request.
         * */
        cancelPmsWriteback: async (transactionId: number) =>
            (await client.delete<string>(`provider/WriteBack/${transactionId}`)).data,

        /**
         * Query for Patients/Responsible Parties that are eligible for bulk import from the PMS.
         * @param signal Optional abort signal to cancel the request.
         * @returns Collection of P/RP entries from the PMS.
         */
        bulkRequestEligible: async (signal?: AbortSignal) =>
            (await client.get<BulkRequestQueryResponse>("provider/bulkRequestEligible", { signal })).data,

        /**
         * Submit a Bulk Request that will process all of the specified items asynchronously.
         * @param items The items to process.
         */
        bulkRequest: async (items: BulkRequestItem[]) => {
            await client.post("provider/bulk", { items });
        },

        /**
         * Get statuses of provider's batches.
         * @param signal Optional abort signal to cancel the request.
         * @returns Collection of recent batches, starting with the most recent.
         */
        bulkRequestSummaries: async (signal?: AbortSignal) =>
            (await client.get<BulkRequestSummaries>("provider/batches", { signal })).data,

        /**
         * Enable or disable communication for a patient identified by VyneSync id.
         * @param patientId VyneSync patient identifier.
         * @param allowCommunication Determines whether or not communication to the patient will be allowed.
         * @returns Collection of recent batches, starting with the most recent.
         */
        bulkAllowComm: async (patientId: string, allowCommunication: boolean) => {
            await client.put(`provider/BulkAllowComm/${patientId}`, allowCommunication, {
                headers: { "Content-Type": "application/json" },
            });
        },

        /**
         * Get preview payment request email for provider.
         * @returns Collection of recent batches, starting with the most recent.
         */
        getPreviewPaymentRequest: async (signal?: AbortSignal) =>
            (await client.get<ProviderNotificationPreviewResult>(`provider/preview/payment-request`, { signal })).data,

        /**
         * Query provider's PMS for recent appointments.
         * @param signal Optional abort signal to cancel the request.
         * @returns Provider's recent appointments.
         */
        appointments: async (signal?: AbortSignal) =>
            (await client.get<AppointmentResults>("provider/appointments", { signal })).data,

        /**
         * Provision a Vyne Patient Payments P/RP for a PMS patient, if there isn't one already.
         * @param pmsId Identifies the PMS patient for whom to provision a P/RP.
         * @param signal Optional abort signal to cancel the request.
         * @returns P/RP identifier (whether already existing or newly created).
         */
        provision: async (pmsId: string) => (await client.post<number>(`provider/provision/${pmsId}`)).data,

        /** Create Stripe connected account for on-boarding. Can be safely called without creating duplicate accounts. */
        onboarding: async () => (await client.post<OnboardingResult>(`provider/connectedAccount/onboarding`)).data,

        /** Get Stripe on-boarding status, and link needed to continue enrollment if necessary. */
        onboardingStatus: async (signal?: AbortSignal) =>
            (await client.get<OnboardingResult>(`provider/connectedAccount/onboarding`, { signal })).data,

        /** Submit legal business info for on-boarding. */
        getLegalBusinessInfo: async (signal?: AbortSignal) =>
            (await client.get<LegalBusinessInfoResult>(`/provider/settings/opera/business-entity`, { signal })).data,

        /** Submit legal business info for on-boarding. */
        submitLegalBusinessInfo: async (data: BusinessEntityInfo) =>
            (await client.put<LegalBusinessInfoResult>(`/provider/settings/opera/business-entity`, data)).data,

        /**
         * Create a new Stripe Connected Account session, used for e.g. payout reporting.
         * @returns Data used to access the session (primarily client secret).
         */
        createSession: async () =>
            (await client.post<ConnectedAccountSessionResponse>("provider/connectedAccount/session")).data,

        /**
         * Create Stripe settings form link.
         * @returns URL to the Stripe settings form.
         */
        stripeSettingsFormLink: async () =>
            (await client.post<StripeSettingsFormLinkResult>("Provider/ConnectedAccount/Form")).data,

        /** Get status of the Stripe connected account. */
        stripeAccountStatus: async (signal?: AbortSignal) =>
            (await client.get<StripeAccountStatusResult>("Provider/ConnectedAccount/Status", { signal })).data,

        /** Get default Stripe fees. */
        stripeDefaultFees: async (signal?: AbortSignal) =>
            (await client.get<StripeRatesResult>("provider/settings/stripe/default-fees", { signal })).data,

        /** Query insurance carriers for a provider. */
        queryInsurance: async (query: string, signal?: AbortSignal) =>
            // analogous to queryPatients
            (
                await client.get<ExistingInsuranceResult[]>("provider/insurance", {
                    params: { query },
                    signal,
                })
            ).data,

        /** Provision insurance to be used in a payment. */
        provisionInsurancePayment: async (request: ProvisionInsurancePaymentRequest) =>
            (await client.post<number>("provider/insurance", request)).data,

        /** Get Stripe practice rates. */
        stripePracticeRates: async (signal?: AbortSignal) =>
            (await client.get<StripeRatesResult>("settings/practice/stripe/fees", { signal })).data,
    },
    transaction: {
        /**
         * Fully refund an individual Transaction. Will always attempt to Void first and only Return if the batch has
         * already settled. Only used by GPI transactions now.
         * @param transactionId The ID of the Transaction to refund.
         */
        refund: async (transactionId: number) =>
            (await client.post<TransactionRefundResult>(`transaction/${transactionId}/refund`)).data,

        /**
         * Partially refund an individual Transaction. Only applicable for Stripe transactions.
         * @param transactionId The ID of the Transaction to refund.
         * @param amount The amount to be refunded.
         */
        partialRefund: async (transactionId: number, amount: StrictlyBasisPoints) =>
            (await client.post<TransactionRefundResult>(`transaction/${transactionId}/partialRefund`, { amount })).data,

        /**
         * Get status of a refund. Only works for Stripe refunds.
         * @param transactionId The ID of the Transaction that was refunded.
         * @param pprid object containing the Stripe refund id that we're inquiring about.
         * @param signal Optional abort signal to cancel the request.
         */
        refundStatus: async ({ transactionId, paymentProcessorId }: RefundStatusRequest, signal?: AbortSignal) =>
            (
                await client.get<TransactionRefundResult>(
                    `transaction/${transactionId}/refund/${paymentProcessorId.paymentProcessor}/${paymentProcessorId.id}`,
                    { signal },
                )
            ).data,
    },
    terminals: {
        /** Get list of Terminals related to a Provider */
        list: async (signal?: AbortSignal) => (await client.get<TerminalResult[]>(`terminals`, { signal })).data,

        /**
         * Create a Stripe Terminal
         * @param terminalCreateRequest Request body for creating a Terminal.
         */
        create: async (terminalCreateRequest: TerminalCreateRequest) =>
            (await client.post<TerminalResult>(`terminals`, terminalCreateRequest)).data,

        /**
         * Update a Stripe Terminal
         * @param terminalUpdateRequest Request body for updating a Terminal.
         */
        update: async (terminalUpdateRequest: TerminalUpdateRequest) =>
            (
                await client.put<TerminalResult>(`terminals/${terminalUpdateRequest.id}`, {
                    terminalLabel: terminalUpdateRequest.terminalLabel,
                })
            ).data,

        /**
         * Delete a Stripe Terminal
         * @param terminalId The ID of the Terminal to delete.
         */
        delete: async (terminalId: string) => (await client.delete<string>(`terminals/${terminalId}`)).data,
    },
};
